import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Badge, Button, Checkbox, Col, Grid, Heading, Input, Menu, Pagination, Select, Spinner, Table, Td, Text, Th, Tr,
} from '@ui'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getJSON, patchJSON } from '../api.js'
import { GpxExportButton } from '../components/GpxExport.jsx'
import { MediaIcons } from '../components/Photos.jsx'
import { ShareIcon } from '../components/ShareControl.jsx'
import { StarToggle, withStar } from '../components/Star.jsx'
import {
  COMPARISON_SOURCES, formatDate, formatDistance, formatDuration, formatNumber, formatPace, formatTime, sourceLabel,
  ukYear,
} from '../format.js'
import { useStoredState } from '../storage.js'
import { useUnit } from '../units.jsx'

const PAGE_SIZE = 50

// Every column the table can show. `sort` gives the value to sort by; `defaultOn` is the first-
// visit choice (the viewer's own choice is remembered).
const COLUMNS = [
  { key: 'date', label: 'Date', defaultOn: true, sort: (w) => w.start_time },
  { key: 'name', label: 'Name', defaultOn: true, sort: (w) => w.name.toLowerCase() },
  { key: 'source', label: 'Recorded with', defaultOn: true, sort: (w) => sourceLabel(w) },
  { key: 'distance', label: 'Distance', defaultOn: true, numeric: true, sort: (w) => w.distance_meters },
  { key: 'time', label: 'Moving time', defaultOn: true, numeric: true, sort: (w) => w.moving_duration_ms },
  { key: 'pace', label: 'Pace', defaultOn: true, numeric: true, sort: (w) => (w.distance_meters ? w.moving_duration_ms / w.distance_meters : Infinity) },
  { key: 'climb', label: 'Climb', defaultOn: true, numeric: true, sort: (w) => w.elevation_gain_meters ?? -1 },
  { key: 'steps', label: 'Steps', defaultOn: false, numeric: true, sort: (w) => w.steps ?? -1 },
  { key: 'brisk', label: 'Brisk minutes', defaultOn: false, numeric: true, sort: (w) => w.brisk_minutes ?? -1 },
  { key: 'recordings', label: 'Recordings', defaultOn: true, numeric: true, sort: (w) => w.group_size },
  { key: 'photos', label: 'Photos', defaultOn: false, numeric: true, sort: (w) => w.photo_count - (w.video_count ?? 0) },
  { key: 'videos', label: 'Videos', defaultOn: false, numeric: true, sort: (w) => w.video_count ?? 0 },
  { key: 'notes', label: 'Notes', defaultOn: true, sort: (w) => (w.notes ?? '').toLowerCase() },
]

const DEFAULT_VISIBLE = COLUMNS.filter((c) => c.defaultOn).map((c) => c.key)

// The name cell: star, name, photo/video markers, a share marker while a link works (2026-10-25)
// with its notes after it, and why the row isn't counted.
function NameCell({ walk, onStar, reason }) {
  return (
    <td>
      <StarToggle quiet starred={walk.starred} onChange={(on) => onStar(walk, on)} />
      {walk.name}<MediaIcons walk={walk} />
      {walk.shared && <ShareIcon notes={walk.share_notes} ms={1} />}
      {walk.share_notes && <Text muted text="small" ms={1}>{walk.share_notes}</Text>}
      {reason && <>{' '}<Badge weight="normal">{reason}</Badge></>}
    </td>
  )
}

function Cell({ column, walk, unit, onStar }) {
  switch (column) {
    case 'date':
      return <Td nowrap>{formatDate(walk.start_time)} <Text muted>{formatTime(walk.start_time)}</Text></Td>
    case 'name':
      return <NameCell walk={walk} onStar={onStar} reason={notCountedReason(walk)} />
    case 'source':
      return <Td nowrap>{sourceLabel(walk)}</Td>
    case 'distance':
      return <Td numeric nowrap>{formatDistance(walk.distance_meters, unit)}</Td>
    case 'time':
      return <Td numeric>{formatDuration(walk.moving_duration_ms)}</Td>
    case 'pace':
      return <Td numeric>{formatPace(walk.moving_duration_ms, walk.distance_meters, unit)}</Td>
    case 'climb':
      return <Td numeric nowrap>{walk.elevation_gain_meters == null ? '–' : `${formatNumber(walk.elevation_gain_meters)} m`}</Td>
    case 'steps':
      return <Td numeric>{formatNumber(walk.steps)}</Td>
    case 'brisk':
      return <Td numeric>{formatNumber(walk.brisk_minutes)}</Td>
    case 'recordings':
      return <Td numeric>{walk.group_size > 1 ? walk.group_size : ''}</Td>
    case 'photos':
      return <Td numeric>{walk.photo_count - (walk.video_count ?? 0) || ''}</Td>
    case 'videos':
      return <Td numeric>{walk.video_count || ''}</Td>
    case 'notes':
      return <Td truncate style={{ maxWidth: '18rem' }} title={walk.notes ?? ''}>{walk.notes}</Td>
    default:
      return <td />
  }
}

// Why a row isn't counted in totals, or null if it is.
function notCountedReason(walk) {
  if (walk.excluded_from_analysis) return 'excluded'
  if (walk.has_mock_location) return 'test trace'
  if (COMPARISON_SOURCES[walk.source]) return 'comparison'
  if (!walk.is_counted) return 'duplicate recording'
  return null
}

export default function Walks() {
  const [unit] = useUnit()
  const navigate = useNavigate()
  const [walks, setWalks] = useState(null)
  const [error, setError] = useState(null)
  const [year, setYear] = useState('all')
  const [withPhotos, setWithPhotos] = useState(false)
  const [show, setShow] = useStoredState('walks.show', 'counted')
  const [visible, setVisible] = useStoredState('walks.columns', DEFAULT_VISIBLE)
  const [sort, setSort] = useState({ key: 'date', desc: true })
  const [page, setPage] = useState(1)
  // ?source=strava_import (the landing page's per-app links): that app's recordings only, every one
  // of them, since "Each walk once" leaves comparison recordings and duplicates out.
  const [params, setParams] = useSearchParams()
  const app = params.get('source')
  // The search is copied to the address (?q=), so Back from a walk returns to the same results. The
  // box keeps its own text: driven from the address it lost keystrokes typed faster than it updated.
  const [search, setSearch] = useState(() => params.get('q') ?? '')
  const setParam = (key, value) => setParams((current) => {
    const next = new URLSearchParams(current)
    if (value) next.set(key, value)
    else next.delete(key)
    return next
  }, { replace: true })
  const which = app ? 'all' : show
  // ?starred=1: starred walks only, kept in the address so the filtered list can be bookmarked.
  const starredOnly = params.get('starred') === '1'
  // ?shared=1: walks with a working public link (2026-10-25), likewise.
  const sharedOnly = params.get('shared') === '1'

  useEffect(() => {
    const controller = new AbortController()
    getJSON('/api/walks', { signal: controller.signal })
      .then(setWalks)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    return () => controller.abort()
  }, [])

  // Shown at once on every recording of the walk; the list is then reloaded, which also puts it
  // right if the change failed.
  const setStar = (walk, starred) => {
    setWalks((current) => withStar(current, walk, starred))
    patchJSON(`/api/walks/${walk.id}`, { starred })
      .catch(() => {})
      .then(() => getJSON('/api/walks'))
      .then(setWalks)
      .catch(() => {})
  }

  const years = useMemo(
    () => (walks ? [...new Set(walks.map((w) => ukYear(w.start_time)))].sort((a, b) => b - a) : []),
    [walks],
  )

  // Every filter but "Each walk once", so the page can say how many more "Every recording" would show.
  const matching = useMemo(() => {
    if (!walks) return []
    const needle = search.trim().toLowerCase()
    return walks
      .filter((w) => !app || w.source === app)
      .filter((w) => year === 'all' || ukYear(w.start_time) === Number(year))
      .filter((w) => !withPhotos || w.photo_count > 0)
      .filter((w) => !starredOnly || w.starred)
      .filter((w) => !sharedOnly || w.shared)
      .filter((w) => !needle || [w.name, w.notes, w.share_notes, sourceLabel(w), w.device_manufacturer]
        .some((text) => (text ?? '').toLowerCase().includes(needle)))
  }, [walks, search, year, app, withPhotos, starredOnly, sharedOnly])
  const filtered = useMemo(() => {
    const column = COLUMNS.find((c) => c.key === sort.key) ?? COLUMNS[0]
    return matching
      .filter((w) => which === 'all' || w.is_counted)
      .sort((a, b) => {
        const x = column.sort(a)
        const y = column.sort(b)
        const order = x < y ? -1 : x > y ? 1 : 0
        return sort.desc ? -order : order
      })
  }, [matching, which, sort])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const countedMeters = filtered.filter((w) => w.is_counted).reduce((sum, w) => sum + w.distance_meters, 0)
  const shownColumns = COLUMNS.filter((c) => visible.includes(c.key))

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key === 'date' }))
    setPage(1)
  }
  const toggleColumn = (key) =>
    setVisible((v) => (v.includes(key) ? v.filter((k) => k !== key) : [...v, key]))

  if (error) return <Alert variant="danger">Couldn't load walks: {error}</Alert>
  if (!walks) return <Spinner />

  return (
    <>
      <Heading size={3}>Walks</Heading>
      <Grid gap={2} align="center" mb={3}>
        <Col xs={12} md={4}>
          <Input
            type="search" placeholder="Search names, notes, app or phone" aria-label="Search names, notes, app or phone"
            value={search} onChange={(e) => { setSearch(e.target.value); setParam('q', e.target.value); setPage(1) }}
          />
        </Col>
        <Col xs="auto">
          <Select aria-label="Year" value={year} onChange={(e) => { setYear(e.target.value); setPage(1) }}>
            <option value="all">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
        </Col>
        <Col xs="auto">
          {app ? (
            <Button
              size="sm" variant="secondary" outline onClick={() => { setParam('source', null); setPage(1) }}
              aria-label={`Show every app, not just ${sourceLabel({ source: app })}`} title="Show every app"
            >
              App: {sourceLabel({ source: app })} ×
            </Button>
          ) : (
            <Select aria-label="Which walks" value={show} onChange={(e) => { setShow(e.target.value); setPage(1) }}>
              <option value="counted">Each walk once</option>
              <option value="all">Every recording</option>
            </Select>
          )}
        </Col>
        <Col xs="auto">
          <Checkbox
            id="with-photos" label="With photos or videos" checked={withPhotos}
            onChange={(e) => { setWithPhotos(e.target.checked); setPage(1) }}
          />
        </Col>
        <Col xs="auto">
          <Checkbox
            id="starred" label="Starred" checked={starredOnly}
            onChange={(e) => { setParam('starred', e.target.checked ? '1' : null); setPage(1) }}
          />
        </Col>
        <Col xs="auto">
          <Checkbox
            id="shared" label="Shared" checked={sharedOnly}
            onChange={(e) => { setParam('shared', e.target.checked ? '1' : null); setPage(1) }}
          />
        </Col>
        <Col xs="auto">
          <Menu label="Columns">
              {COLUMNS.map((c) => (
                <Checkbox
                  key={c.key} id={`col-${c.key}`} label={c.label}
                  checked={visible.includes(c.key)} onChange={() => toggleColumn(c.key)}
                />
              ))}
          </Menu>
        </Col>
        <Col xs="auto">
          <GpxExportButton walks={walks} year={year} everyRecording={which === 'all'} />
        </Col>
      </Grid>

      <Text as="p" muted text="small" mb={2}>
        {filtered.length.toLocaleString('en-GB')} {which === 'all' ? 'recordings' : 'walks'}
        {app && ` from ${sourceLabel({ source: app })}`},{' '}
        {formatDistance(countedMeters, unit, 1)}
        {which === 'all' && ' counted in totals'}.
        {which === 'counted' && search.trim() && matching.length > filtered.length && (
          <> {(matching.length - filtered.length).toLocaleString('en-GB')} more {matching.length - filtered.length === 1 ? 'recording matches' : 'recordings match'} under “Every recording” (other phones’ and comparison apps’ recordings).</>
        )}
        {which === 'counted' && ' Where a walk was recorded more than once, this shows the recording the monthly totals count (MapMyWalk’s if there is one, else the longest).'}
      </Text>

      <Table hover responsive middle>
        <thead>
          <tr>
            {shownColumns.map((c) => (
              <Th
                key={c.key}
                numeric={c.numeric}
                nowrap
                aria-sort={sort.key === c.key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
              >
                <Button variant="link" p={0} inheritColor weight="semibold" noUnderline onClick={() => toggleSort(c.key)}>
                  {c.label}{sort.key === c.key ? (sort.desc ? ' ▾' : ' ▴') : ''}
                </Button>
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => {
            const reason = notCountedReason(w)
            return (
              <Tr
                key={w.id}
                role="link"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                muted={!!reason}
                onClick={() => navigate(`/walks/${w.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/walks/${w.id}`) }}
              >
                {shownColumns.map((c) => <Cell key={c.key} column={c.key} walk={w} unit={unit} onStar={setStar} />)}
              </Tr>
            )
          })}
        </tbody>
      </Table>

      {pageCount > 1 && (
        <Pagination page={currentPage} count={pageCount} onChange={setPage} />
      )}
    </>
  )
}
